import { apiClient } from '@/shared/lib/api/apiClient';
import type { LoginResponse } from '@/shared/types/types';
import type { AccountModel, AccountsPage, AccountDetail, AccountListParams, AccountInstallationToken } from '../types';

interface ApiResponse<T> { data: T; success: boolean; message: string }

function paginationToParams(params: AccountListParams): Record<string, string> {
  const out: Record<string, string> = {};
  if (params.search) out.search = params.search;
  if (params.page) out.page = String(params.page);
  if (params.pageSize) out.pageSize = String(params.pageSize);
  return out;
}

export const accountsApi = {
  async getChildren(params: AccountListParams): Promise<AccountsPage> {
    const res = await apiClient.get<ApiResponse<AccountsPage>>('/accounts/children', paginationToParams(params));
    return res.data;
  },
  async getAccount(id: string): Promise<AccountDetail> {
    const res = await apiClient.get<ApiResponse<AccountDetail>>(`/accounts/${id}`);
    return res.data;
  },
  async createChild(payload: { nome: string; accountModel: AccountModel; adminNome: string; adminEmail: string; document?: string | null; legalName?: string | null }): Promise<{ id: string }> {
    const res = await apiClient.post<ApiResponse<{ id: string }>>('/accounts/children', payload);
    return res.data;
  },
  changeAccountType(id: string, targetModel: AccountModel, password: string): Promise<void> {
    return apiClient.patch<void>(`/accounts/${id}/type`, { targetModel, password });
  },
  update(id: string, payload: { nome: string; document: string | null; responsibleUser: string | null; legalName?: string | null }): Promise<void> {
    return apiClient.patch<void>(`/accounts/${id}`, payload);
  },
  setStatus(id: string, ativo: boolean): Promise<void> {
    return apiClient.patch<void>(`/accounts/${id}/status`, { ativo });
  },
  async getUninstallPin(id: string): Promise<string> {
    const res = await apiClient.get<ApiResponse<{ pin: string }>>(`/accounts/${id}/uninstall-pin`);
    return res.data.pin;
  },
  async regenerateUninstallPin(id: string): Promise<string> {
    const res = await apiClient.post<ApiResponse<{ pin: string }>>(`/accounts/${id}/uninstall-pin/regenerate`);
    return res.data.pin;
  },
  async getInstallationTokens(id: string, page = 1, pageSize = 50): Promise<{ tokens: AccountInstallationToken[]; totalCount: number }> {
    const res = await apiClient.get<ApiResponse<{ tokens: AccountInstallationToken[]; totalCount: number }>>(
      `/accounts/${id}/installation-tokens`, { page: String(page), pageSize: String(pageSize) });
    return res.data;
  },
  async createInstallationToken(id: string, payload: { expiresInDays?: number; maxUses?: number }): Promise<AccountInstallationToken> {
    const res = await apiClient.post<ApiResponse<AccountInstallationToken>>(`/accounts/${id}/installation-tokens`, payload);
    return res.data;
  },
  // Gestao do token da conta-filha: mira /accounts/{id}/installation-tokens/{tokenId}, cuja
  // autoridade desce pelo gerador (o Root/pai gerencia o token que gerou) — as rotas globais
  // /installation-tokens escopam pelo tenant do caller e nao alcancam o token da filha.
  async updateInstallationToken(id: string, tokenId: string, payload: { maxUses?: number; expiresAt?: string; clearExpiresAt?: boolean }): Promise<AccountInstallationToken> {
    const res = await apiClient.put<ApiResponse<AccountInstallationToken>>(`/accounts/${id}/installation-tokens/${tokenId}`, payload);
    return res.data;
  },
  deactivateInstallationToken(id: string, tokenId: string): Promise<void> {
    return apiClient.patch<void>(`/accounts/${id}/installation-tokens/${tokenId}/deactivate`);
  },
  activateInstallationToken(id: string, tokenId: string): Promise<void> {
    return apiClient.patch<void>(`/accounts/${id}/installation-tokens/${tokenId}/activate`);
  },
  deleteInstallationToken(id: string, tokenId: string): Promise<void> {
    return apiClient.delete<void>(`/accounts/${id}/installation-tokens/${tokenId}`);
  },
  // Acting view para contas-pai: o backend devolve uma sessao nova com os
  // claims da conta assumida; aplicar via authService.completeLogin, igual
  // ao fluxo do platform admin.
  assumeView(tenantId: string): Promise<LoginResponse> {
    return apiClient.put<LoginResponse>('/accounts/acting-view', { tenantId });
  },
  releaseView(): Promise<LoginResponse> {
    return apiClient.delete<LoginResponse>('/accounts/acting-view');
  },
};
